import { CalendarCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { ForwardArrow } from "#/components/ui";
import { trackFabShown } from "#/lib/analytics";
import { useI18n } from "#/lib/i18n";

/**
 * Floating "book my seat" button. Hidden on first paint (so it matches SSR and
 * doesn't crowd the hero); slides in once the visitor has scrolled past the fold.
 * Sits bottom-end, opposite the WhatsApp button (bottom-start).
 */
export function BookingFab({ href = "#register" }: { href?: string }) {
	const { t } = useI18n();
	const [show, setShow] = useState(false);

	useEffect(() => {
		let announced = false;
		const onScroll = () => {
			const visible = window.scrollY > 480;
			setShow(visible);
			// Report the impression once, so the FAB gets a real click-through rate.
			if (visible && !announced) {
				announced = true;
				trackFabShown("booking_fab");
			}
		};
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	return (
		<>
			{/* biome-ignore lint/a11y/useAnchorContent: label is dynamic (t.nav.book); aria-label is set */}
			<a
				href={href}
				aria-label={t.nav.book}
				data-track="booking_fab"
				data-track-type="schedule"
				data-track-section="floating"
				aria-hidden={!show}
				tabIndex={show ? 0 : -1}
				// Sits on the bottom-left, stacked directly above the Messenger button.
				className={`group fixed bottom-24 left-5 z-50 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3.5 text-sm font-bold text-white shadow-lift transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-teal ${
					show
						? "translate-y-0 opacity-100"
						: "pointer-events-none translate-y-5 opacity-0"
				}`}
			>
				<CalendarCheck className="h-4.5 w-4.5 text-gold" />
				{t.nav.book}
				<ForwardArrow className="h-3.5 w-3.5 transition-transform rtl:group-hover:-translate-x-0.5 ltr:group-hover:translate-x-0.5" />
			</a>
		</>
	);
}
